<template>
  <div class="imageSelectionContainer">
    <AvatarHand
      v-if="props.isAvatarsTurn"
      :wait-time="props.waitTime * 0.9"
      :promptLocation="promptPosition"
      :moveToLocation="props.waitingForAnswer"
      @answer-selected="
        onSelection(correctAnswer as number, shuffledOptions[correctAnswer as number], true)
      "
    />
    <div
      v-for="(option, index) in shuffledOptions"
      :data-index="index"
      class="imageSelectionOption"
      :key="option.imageUrl"
      :ref="(el) => (imageRefs[index] = el as HTMLDivElement)"
      @click.prevent="onSelection(index, option)"
      @touchstart.prevent="onSelection(index, option)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import type { OptionsEntity, progressSateError } from '@/customTypes'
import type { Ref, ComputedRef } from 'vue'

// Store
import { useDataStore } from '@/stores/DataStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { usePreloadStore } from '@/stores/PreloadStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'

//Helpers
import { useTimeout } from '@/utils/helpers'

// Services
import Logger from '@/services/Logger.js'

const logger: any = new Logger('ImageSelection', true)
import AvatarHand from '@/components/AvatarHand.vue'

const props = defineProps({
  options: {
    type: Array as () => OptionsEntity[],
    default: () => []
  },
  waitTime: {
    type: Number,
    default: 0
  },
  waitingForAnswer: {
    type: Boolean,
    default: false
  },
  isPracticeQuestion: {
    type: Boolean,
    default: false
  },
  isAvatarsTurn: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  (e: 'image-selected', answered: boolean): void
  (e: 'play-prompt'): void
}>()

const selectAnimationTime = 500
const imageRefs: Ref<HTMLDivElement[]> = ref([])
const selectedOption: Ref<OptionsEntity | null> = ref(null)

const attemptCount: Ref<number> = ref(1)
const showHighlight: Ref<boolean> = ref(false)
const locked: Ref<boolean> = ref(false)
const userUnresponsive: Ref<boolean> = ref(false)

const preloadStore = usePreloadStore()
const { preloadedImages } = storeToRefs(preloadStore)
const dataStore = useDataStore()
const { screenerTemplateId, isOffline } = storeToRefs(dataStore)
const soundEffectsStore = useSoundEffectsStore()
const { setManagedTimeout } = useTimeout()
const activeScreenerStore = useActiveScreenerStore()

const correctAnswer: Ref<Number> = ref(0)
const promptPosition: Ref<number> = ref(0)

const imageLoadingStates: Ref<Record<number, 'loading' | 'loaded' | 'error'>> = ref({})
const maxRetries = 3
const retryDelay = 3000
const COMPONENT_ERROR_TYPE: progressSateError = 'UserUnresponsive'

const shuffledOptions: ComputedRef<OptionsEntity[]> = computed(() => {
  let array = [...props.options].sort(() => Math.random() - 0.5)
  return array as OptionsEntity[]
})

const selectable: ComputedRef<boolean> = computed(() => {
  return props.waitingForAnswer && !locked.value && !props.isAvatarsTurn
})

const screenerHasComponentError = computed(() =>
  activeScreenerStore.hasErrorState(COMPONENT_ERROR_TYPE)
)
async function finish(): Promise<void> {
  lockSelection()
  if (userUnresponsive.value || screenerHasComponentError.value) {
    userUnresponsive.value = false
    sendResponsiveNotification()
  }

  clearHighlight()
  const correctAnswer = !!selectedOption.value?.correct
  emit('image-selected', correctAnswer)
  if (!props.isAvatarsTurn) {
    dataStore.onSelectAnswer(selectedOption.value as OptionsEntity | null)
  }
}

watch(
  () => showHighlight.value,
  (newVal) => {
    if (newVal === false) return
    highlightCorrectAnswer()
  }
)

watch(
  () => props.waitingForAnswer,
  (newVal) => {
    if (newVal === false || props.isAvatarsTurn) return
    waitForSelection()
  }
)

watch(isOffline, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    // Network is back online
    shuffledOptions.value.forEach((option, index) => {
      if (imageLoadingStates.value[index] === 'error') {
        const imageUrl = typeof option === 'string' ? option : (option.imageUrl as string)
        retryLoadImage(index, imageUrl)
      }
    })
  }
})

onMounted(async () => {
  preloadStore.loadNextQuestionAssets()

  imageRefs.value.forEach((ref, index) => {
    const imageData = shuffledOptions.value[index]
    const imageUrl = typeof imageData === 'string' ? imageData : (imageData.imageUrl as string)
    const preloadedImage = preloadedImages.value[imageUrl]
    if (preloadedImage) {
      ref.appendChild(preloadedImage)
      imageLoadingStates.value[index] = 'loaded'
    } else {
      loadImage(index, imageUrl)
    }
    if (imageData.correct) {
      correctAnswer.value = index
      if (props.isAvatarsTurn) {
        promptPosition.value = ref.getBoundingClientRect().left
      }
    }
  })
  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  imageRefs.value.forEach((ref) => {
    while (ref.firstChild) {
      ref.removeChild(ref.firstChild)
    }
  })

  imageRefs.value.forEach((ref, index) => {
    const imageData = shuffledOptions.value[index]
    const imageUrl = typeof imageData === 'string' ? imageData : (imageData.imageUrl as string)
    preloadStore.deleteImageFromCache(imageUrl)
  })

  window.removeEventListener('resize', onResize)
  dataStore.clearAllLoadErrorTimeouts()
})

function waitForSelection(): void {
  setManagedTimeout(() => {
    attemptCount.value++
    if (!selectedOption.value && props.isPracticeQuestion && attemptCount.value === 1) {
      emit('play-prompt')

      highlightCorrectAnswer()
    } else if (!selectedOption.value && props.isPracticeQuestion && attemptCount.value === 2) {
      lockSelection()
      finish()
    } else if (!selectedOption.value && !props.isPracticeQuestion) {
      if (attemptCount.value >= 3) {
        if (!userUnresponsive.value && !selectedOption.value) {
          sendUnresponsiveNotification()
        }
      }
      emit('play-prompt')
    }
  }, props.waitTime * 1050)
}

function getImageUrl(fileName: string): string {
  return `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/question-assets/${fileName}?v=${_ASSETS_VERSION_}`
}

function lockSelection(): void {
  locked.value = true

  if (selectedOption.value) {
    const selectedIndex = shuffledOptions.value.findIndex(
      (option) => option === selectedOption.value
    )

    imageRefs.value.forEach((ref, index) => {
      if (index !== selectedIndex) {
        ref.classList.add('faded')
      }
    })
  }
}

const onSelection = (
  index: number,
  option: OptionsEntity,
  avatarOverride: boolean = false
): void => {
  if (!selectable.value && !avatarOverride) return
  lockSelection()
  clearHighlight()
  soundEffectsStore.playTrack('selectionSound')
  selectedOption.value = option
  imageRefs.value[index]?.classList.add('selected')
  setManagedTimeout(() => {
    finish()
  }, selectAnimationTime)
}

const highlightCorrectAnswer = (): void => {
  showHighlight.value = true
  const index = correctAnswer.value as number
  const img = imageRefs.value[index]?.querySelector('img')
  if (img) img.classList.add('highlight')
}

const clearHighlight = (): void => {
  showHighlight.value = false
  const index = correctAnswer.value as number
  const img = imageRefs.value[index]?.querySelector('img')
  if (img) img.classList.remove('highlight')
}

const onResize = () => {
  if (props.isAvatarsTurn) {
    promptPosition.value =
      imageRefs.value[correctAnswer.value.valueOf()].getBoundingClientRect().left
  }
}

const retryLoadImage = (index: number, imageUrl: string) => {
  imageLoadingStates.value[index] = 'loading'
  loadImage(index, imageUrl)
}

const loadImage = async (index: number, imageUrl: string, retryCount = 0): Promise<void> => {
  if (retryCount >= maxRetries) {
    imageLoadingStates.value[index] = 'error'
    return
  }

  try {
    const img = new Image()
    img.src = getImageUrl(imageUrl)
    let imageErrorId: string | null = null

    img.onload = () => {
      imageLoadingStates.value[index] = 'loaded'
      imageRefs.value[index]?.appendChild(img)
      if (imageErrorId) {
        dataStore.clearLoadErrorTimeout(imageErrorId)
      }
    }

    //@ts-ignore
    img.onerror = (event: Event) => {
      if (!isOffline.value) {
        setManagedTimeout(() => loadImage(index, imageUrl, retryCount + 1), retryDelay)
        imageErrorId = dataStore.startLoadErrorTimeout(event)
      } else {
        imageLoadingStates.value[index] = 'error'
      }
    }
  } catch (error) {
    logger.error(`Error loading image ${imageUrl}:`, error)
    imageLoadingStates.value[index] = 'error'
  }
}

function sendUnresponsiveNotification() {
  userUnresponsive.value = true
  dataStore.setPausedState(true)
  activeScreenerStore.sendScreenerError({
    type: COMPONENT_ERROR_TYPE,
    message:
      'The user may need help proceeding with the screener, their response timing is longer than expected.',
    priority: 1
  })
}

function sendResponsiveNotification() {
  userUnresponsive.value = false
  activeScreenerStore.sendScreenerResolvedError({
    type: COMPONENT_ERROR_TYPE
  })
}
</script>

<style lang="scss">
.imageSelectionContainer {
  position: relative;
  width: 100%;
  height: calc(var(--full-vh) * 0.65);
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  align-items: center;
  z-index: 1;

  .imageSelectionOption {
    position: relative;
    width: calc(var(--full-vw) * 0.2);
    max-width: 350px;
    max-height: 350px;
    touch-action: manipulation;
    transition: opacity 1s ease-out;

    img {
      width: 100%;
      max-height: 100%;
      background: white;

      box-shadow: 6px 5px 11px 2px rgba(0, 0, 0, 0.4);
      border-radius: 5px;

      z-index: 2;
      &.highlight {
        box-shadow: 0px 0px 15px 10px rgba(250, 250, 2, 0.4);
        @include animation(scaleUp 2s infinite ease-in-out);
      }
    }

    &.faded {
      opacity: 0.2;
    }

    &.selected {
      @include animation(scaleUp v-bind('`${selectAnimationTime}ms`') forwards ease-in-out);
    }
  }

  @include keyframes(scaleUp) {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
}
</style>
